import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Paper,
  IconButton
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MinimizeIcon from "@mui/icons-material/Minimize";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useNotify } from "react-admin";

// ===== GLOBAL TRIGGER =====
let modalTrigger = null;
export const triggerAuditLogModal = () => {
  if (modalTrigger) modalTrigger();
};

// ===== ANIMATIONS =====
const animations = {
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes dots": {
    "0%": { content: '"."' },
    "33%": { content: '".."' },
    "66%": { content: '"..."' },
    "100%": { content: '"."' },
  },
};

const AuditLogGlobalModal = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const eventSourceRef = useRef(null);
  const contentRef = useRef(null);
  const notify = useNotify();

  const openModal = () => {
    setOpen(true);
    setMinimized(false);
  };

  useImperativeHandle(ref, () => openModal);
  modalTrigger = openModal;

  const startSync = async () => {
    setLogs([]);
    setSyncing(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL + "/api/admin/v1/audit-log/sync";
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);

      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      notify(data?.data?.message || "Sinkronisasi audit log dimulai", { type: "info" });
    } catch (err) {
      notify("Terjadi kesalahan: " + err.message, { type: "error" });
      setSyncing(false);
      return;
    }

    const evt = new EventSource(
      process.env.REACT_APP_API_URL + "/api/admin/v1/audit-log/progress"
    );
    eventSourceRef.current = evt;

    evt.onmessage = (e) => {
      const log = JSON.parse(e.data);
      setLogs((prev) => [...prev, log]);

      if (log.status === "FINISHED") {
        evt.close();
        eventSourceRef.current = null;
        setSyncing(false);
        notify("Sinkronisasi selesai!", { type: "success" });
      }
    };

    evt.onerror = () => {
      evt.close();
      eventSourceRef.current = null;
      setSyncing(false);
      notify("Sinkronisasi error SSE", { type: "warning" });
    };
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [logs]);

  const handleClose = () => {
    setOpen(false);
    setMinimized(false);
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setSyncing(false);
  };

  const handleMinimize = () => {
    setOpen(false);
    setMinimized(true);
  };

  const restoreModal = () => {
    setOpen(true);
    setMinimized(false);
  };

  return (
    <>
      {/* ===== MODAL ===== */}
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Kirim Data Depo Ke Pusat</Typography>
            <IconButton size="small" onClick={handleMinimize}>
              <MinimizeIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          ref={contentRef}
          sx={{ maxHeight: 400, overflowY: "auto" }}
        >
          {syncing && <LinearProgress sx={{ mb: 1 }} />}
          {logs.map((log, i) => (
            <Typography key={i}>{`[${log.status}] ${log.message}`}</Typography>
          ))}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>

          {/* ===== BUTTON ANIMATED ===== */}
          <Button
            variant="contained"
            onClick={startSync}
            disabled={syncing}
            startIcon={
              syncing ? (
                <AutorenewIcon
                  sx={{
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                <CloudUploadIcon />
              )
            }
            sx={animations}
          >
            <Typography
              sx={{
                ...(syncing && {
                  "&::after": {
                    content: '"."',
                    animation: "dots 1.2s steps(3, end) infinite",
                  },
                }),
                ...animations,
              }}
            >
              {syncing ? "Syncing" : "Start Sync"}
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== MINIMIZED BAR ===== */}
      {minimized && !open && (
        <Paper
          onClick={restoreModal}
          sx={{
            position: "fixed",
            bottom: 16,
            left: { xs: "50%", md: "auto" },
            right: { md: 16 },
            transform: { xs: "translateX(-50%)" },
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            zIndex: 1300,
            boxShadow: 4,
            bgcolor: "background.paper",
            ...animations,
          }}
        >
          <CloudUploadIcon
            sx={{
              ...(syncing && {
                animation: "spin 1.2s linear infinite",
              }),
            }}
          />
          <Typography
            sx={{
              ...(syncing && {
                "&::after": {
                  content: '"."',
                  animation: "dots 1.2s steps(3, end) infinite",
                },
              }),
            }}
          >
            {syncing ? "Sync running" : "Audit Log Sync"}
          </Typography>
        </Paper>
      )}
    </>
  );
});

export default AuditLogGlobalModal;
